import type { SearchParams } from "next/dist/server/request/search-params";
import { ReadonlyURLSearchParams } from "next/navigation";

import { omitEmpty } from "../../utils/omitEmpty";
import { URLParamEnum } from "../urlParams/types";
import { DEFAULT_FILTERS } from "./constants";
import { isFilters } from "./guards";
import type { Filters } from "./types";

export const getFilters = (
  searchParams: ReadonlyURLSearchParams | SearchParams | URLSearchParams | null,
  overrides?: Partial<Filters>,
): Filters => {
  if (!searchParams) {
    return { ...DEFAULT_FILTERS, ...overrides };
  }

  const filtersObj =
    searchParams instanceof ReadonlyURLSearchParams || searchParams instanceof URLSearchParams
      ? searchParams.get(URLParamEnum.Filters)
      : searchParams[URLParamEnum.Filters];

  if (typeof filtersObj !== "string") return { ...DEFAULT_FILTERS, ...overrides };

  try {
    const parsedFilters = JSON.parse(filtersObj);

    // todo: pass only known keys
    if (isFilters(parsedFilters)) {
      return { ...DEFAULT_FILTERS, ...parsedFilters, ...overrides };
    }

    console.error(`Parsed object didn't pass filters guard. Object:`, parsedFilters);
    return { ...DEFAULT_FILTERS, ...overrides };
  } catch (error) {
    console.error("Failed to parse filters:", error);
    return { ...DEFAULT_FILTERS, ...overrides };
  }
};

export const areFiltersDefault = (
  currentFilters: Partial<Filters>,
  defaultFilters: Partial<Filters>,
): boolean => {
  const cleanCurrent = omitEmpty(currentFilters);
  const cleanDefault = omitEmpty(defaultFilters);

  const keys1 = Object.keys(cleanCurrent);
  const keys2 = Object.keys(cleanDefault);

  if (keys1.length !== keys2.length) return false;

  for (const key of keys1) {
    if (!keys2.includes(key)) return false;

    const val1 = cleanCurrent[key as keyof Filters];
    const val2 = cleanDefault[key as keyof Filters];

    if (Array.isArray(val1) && Array.isArray(val2)) {
      if (val1.length !== val2.length) return false;
      if ([...val1].sort().join(",") !== [...val2].sort().join(",")) return false;
    } else if (val1 !== val2) {
      return false;
    }
  }
  return true;
};
